import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { LeadStatus } from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { LEAD_KANBAN_COLUMNS } from "@/lib/statusConfig";
import { GripVertical, Eye, LayoutGrid, List, Users } from "lucide-react";

interface LeadCardData {
  id: string;
  leadNumber: string;
  status: LeadStatus;
  contactName?: string | null;
  contactEmail?: string | null;
  tripTitle?: string | null;
  destinationName?: string | null;
  paxAdults: number;
  paxChildren: number;
  estimatedValue?: number | null;
  leadScore: number;
}

interface FacetedLeads {
  data: LeadCardData[];
}

export default function LeadKanbanPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dragged, setDragged] = useState<LeadCardData | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["leads", "kanban"],
    queryFn: () => api.get<FacetedLeads>("/api/admin/leads?limit=200"),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) =>
      api.put(`/api/admin/leads/${id}`, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["leads", "kanban"] });
      const prev = queryClient.getQueryData<FacetedLeads>(["leads", "kanban"]);
      queryClient.setQueryData<FacetedLeads>(["leads", "kanban"], (old) =>
        old ? { data: old.data.map((l) => (l.id === id ? { ...l, status } : l)) } : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["leads", "kanban"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  const leads = data?.data ?? [];
  const byStatus = LEAD_KANBAN_COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = leads.filter((l) => l.status === col.id);
      return acc;
    },
    {} as Record<string, LeadCardData[]>,
  );
  const lostCount = leads.filter((l) => l.status === "lost").length;

  const onDrop = (e: React.DragEvent, target: LeadStatus) => {
    e.preventDefault();
    setDragOver(null);
    if (dragged && dragged.status !== target) {
      updateStatus.mutate({ id: dragged.id, status: target });
    }
    setDragged(null);
  };

  if (error)
    return <div className="card text-error-600 text-center">{t("crm.leadKanban.loadError")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("crm.leadKanban.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("crm.leadKanban.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads" className="btn-ghost">
            <List className="h-5 w-5" />
            {t("crm.leadKanban.tableView")}
          </Link>
          <Link to="/leads/kanban" className="btn-primary">
            <LayoutGrid className="h-5 w-5" />
            {t("crm.leads.kanban")}
          </Link>
        </div>
      </div>

      {lostCount > 0 && (
        <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {t("crm.leadKanban.lostCount", { count: lostCount })}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {LEAD_KANBAN_COLUMNS.map((col) => (
            <div
              key={col.id}
              className={`min-w-[280px] flex-1 rounded-xl ${col.bgColor} p-4 transition-all ${
                dragOver === col.id ? "ring-primary-500 ring-2 ring-offset-2" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(col.id);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, col.id)}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${col.color}`} />
                  <h3 className="font-semibold text-gray-900 dark:text-white">{col.label}</h3>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {byStatus[col.id]?.length || 0}
                </span>
              </div>

              <div className="space-y-3">
                {byStatus[col.id]?.map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={() => setDragged(lead)}
                    onDragEnd={() => setDragged(null)}
                    className={`group cursor-grab rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md active:cursor-grabbing dark:bg-gray-800 dark:ring-gray-700 ${
                      dragged?.id === lead.id ? "ring-primary-500 opacity-50 ring-2" : ""
                    }`}
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {lead.contactName || "—"}
                          </p>
                          <p className="font-mono text-xs text-gray-400">{lead.leadNumber}</p>
                        </div>
                      </div>
                      <Link
                        to={`/leads/${lead.id}`}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </div>
                    <p className="line-clamp-1 text-sm text-gray-600 dark:text-gray-300">
                      {lead.tripTitle || lead.destinationName || t("crm.leadKanban.generalInquiry")}
                    </p>
                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-xs dark:border-gray-700">
                      <span className="flex items-center gap-1 text-gray-500">
                        <Users className="h-3 w-3" />
                        {lead.paxAdults + lead.paxChildren}
                      </span>
                      <span className="text-primary-600 font-semibold">
                        {lead.estimatedValue ? formatPrice(lead.estimatedValue) : "—"}
                      </span>
                    </div>
                  </div>
                ))}
                {byStatus[col.id]?.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600">
                    {t("crm.leadKanban.noLeads")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
